/**
 * 이상한 방 - 외우기 → 수색 전환 이펙트
 *
 * 근거: 개발자 요청 2026-09-02 *"원본 방에서 수색으로 넘어갈때 ... 방이 변경되는것처럼 화면이
 * 물결치던지 슈유육 하면서"* + 방식 지정 *"이펙트를 그냥 원본방에서만 주고, 이펙트가 끝남과
 * 동시에 바뀐방으로 전환"*.
 *
 * ## 순서가 곧 공정성이다 (이 파일에서 절대 뒤집지 마라)
 *
 * 전환이 시작되는 순간 **현재 프레임(= 원본 방)을 렌더타깃에 굳혀 캡처**하고, 그 뒤로 화면에
 * 나가는 것은 그 정지 사진 하나뿐이다. 왜곡이 끝나는 순간에야 방을 갈아 끼운다.
 * 그래서 **전환 도중에 변경본의 픽셀이 화면에 닿는 경로 자체가 없다.** 오브젝트가 늦게 나타나
 * 정답을 흘리는 사고는 버그로 막는 것이 아니라 구조로 불가능하다.
 *
 * 두 번째 규칙은 덜 뻔하다. **끊는 순간 화면은 100% 먹색이어야 한다.**
 * 왜곡이 깨끗한 원본으로 되돌아온 뒤 변경본으로 하드컷하면 그건 연출이 아니라
 * **깜빡임 대조기(blink comparator)** 다 - 틀린그림찾기의 정답을 기계적으로 뽑아내는 바로 그 장치다.
 * 그래서 모든 시안의 왜곡 곡선은 끝에서 `uWash`를 정확히 1로 끌어올리고, 교체는 그 아래에서 일어난다.
 * 건너뛰기(탭)도 예외가 아니라 **짧은 먹색 구간을 거쳐** 끝난다(`SKIP_BLANK_MS`).
 *
 * ## 반복 피로가 최대 위험이다
 *
 * 10단계 × 재도전이면 이 전환을 수십 번 본다. 1.5초짜리 멋진 연출은 세 번째부터 방해물이므로
 * 총 길이를 0.6초 언저리(`DISTORT_MS + TAIL_MS`)로 묶고, 탭 한 번에 즉시 접히게 했다.
 *
 * ## 기존 렌더 루프를 건드리지 않는다
 *
 * `EffectComposer`를 도입하지 않는다. 풀스크린 쿼드 하나를 `ctx.scene`에 얹고
 * 정점 셰이더에서 행렬을 통째로 무시해(`gl_Position = vec4(position.xy, 0, 1)`) NDC를 덮는다.
 * `depthTest:false` + 큰 `renderOrder`라 언제나 마지막에 그려지고, `scene.ts`의 rAF 루프는
 * 자기가 하던 대로 `render(scene, camera)`만 부르면 된다.
 *
 * ## 실패해도 전환은 일어난다
 *
 * `play()`는 **성공 여부를 boolean으로 돌려준다.** false면 이펙트가 서지 않은 것이므로
 * 호출부는 원래 쓰던 교체 경로(세션의 디졸브 베일)를 그대로 태우면 된다.
 * 여기서 방을 대신 갈아 끼우지 않는 이유가 그것이다 - 그러면 하드컷이 되어 위의 두 번째 규칙을 깬다.
 *
 * ## 데모와의 계약
 *
 * 아래 GLSL 문자열은 `public/transition-demo.html`에 **그대로** 복사돼 있다(시안 비교용).
 * 정적 페이지라 이 모듈을 import 할 수 없어 생긴 중복이며, 갈라지지 않는지는
 * `transition.test.ts`가 파일을 읽어 검사한다. 셰이더를 고치면 데모도 같이 고쳐야 테스트가 통과한다.
 */

import * as THREE from 'three'

import { prefersReducedMotion } from '../core/env'
import { clamp01 } from '../core/math'
import { MS_PER_SECOND } from '../core/time'
import { INK_HEX, type SceneCtx } from './scene'

/* --- 시안 ----------------------------------------------------------------------- */

/**
 * 시안 넷. 성격 비교는 `public/transition-demo.html`에서 눌러 본다.
 *
 *  - `scan`    스캔: 수평으로 찢어져 어긋나고 노랑 스캔선이 훑고 지나감
 *              (개발자 선택. 방송 은유였던 "채널 전환"을 감식 언어로 옮긴 것이며
 *               보이는 그림은 그대로다 - 현장을 훑는 선으로 읽는다)
 *  - `wave`    물결: 중앙에서 sin 왜곡이 퍼짐
 *  - `shuffle` 셔플: 화면을 격자로 쪼개 타일이 뒤섞임
 *  - `punch`   줌 펀치: 짧은 줌 + 방사 블러
 */
export type TransitionKind = 'scan' | 'wave' | 'shuffle' | 'punch'

export const TRANSITION_KINDS: readonly TransitionKind[] = ['scan', 'wave', 'shuffle', 'punch']

/**
 * 기본 시안. **개발자가 넷을 비교하고 직접 골랐다** - *"채널전환 효과가 좋은것같음"*.
 *
 * 이름만 `scan`으로 옮겼다(방송 은유를 코드에 남기지 않으려는 것이며 그림은 한 픽셀도 안 바뀐다).
 * 나머지 셋은 이 상수 하나로 바꿔 볼 수 있고 셰이더는 전부 `TRANSITION_SHADER_BODY` 표 안에 있다.
 */
export const DEFAULT_TRANSITION_KIND: TransitionKind = 'scan'

/* --- 타이밍 --------------------------------------------------------------------- */

/** 왜곡 길이(ms). 0.6초 예산의 본체. */
const DISTORT_MS = 520

/** 교체 뒤 먹색이 걷히는 길이(ms). 새 방이 통째로 같은 알파로 드러나므로 정답을 흘리지 않는다. */
const TAIL_MS = 90

/** 탭 건너뛰기의 먹색 구간(ms). 0으로 두면 건너뛰기가 곧 깜빡임 대조기가 된다. */
const SKIP_BLANK_MS = 70

/** 움직임 줄이기: 왜곡 없이 먹색으로 덮는 시간(ms). */
const REDUCED_BLANK_MS = 110

/** 움직임 줄이기: 먹색이 걷히는 시간(ms). 왜곡이 없으니 조금 더 여유를 준다. */
const REDUCED_TAIL_MS = 130

/**
 * 시안별 **먹색이 차오르기 시작하는 진행도**.
 *
 * 셔플만 크게 이른(0.60) 이유: 타일이 제자리로 모이는 구간이 어둠 **안에서** 끝나야 한다.
 * 밝은 상태로 다 모이면 깨끗한 원본이 잠깐 서고, 그 직후의 교체가 깜빡임 대조가 된다.
 */
export const TRANSITION_WASH_START: Readonly<Record<TransitionKind, number>> = {
  scan: 0.84,
  wave: 0.82,
  shuffle: 0.6,
  punch: 0.76,
}

/**
 * 타임라인 상수 묶음. 데모 페이지가 같은 값을 쓰고 있는지 테스트가 여기로 대조한다.
 * 총 길이는 `distort + tail`이고 건너뛰기는 어느 지점에서든 `skipBlank + tail` 안에 끝난다.
 */
export const TRANSITION_TIMING_MS = {
  distort: DISTORT_MS,
  tail: TAIL_MS,
  skipBlank: SKIP_BLANK_MS,
  reducedBlank: REDUCED_BLANK_MS,
  reducedTail: REDUCED_TAIL_MS,
} as const

/**
 * 먹색 차오름 곡선. `progress`가 1이면 **반드시 1**이다(위 두 번째 규칙의 집행 지점).
 * 제곱을 쓰는 이유는 늦게 시작해 빠르게 덮는 편이 페이드아웃이 아니라 컷으로 읽히기 때문이다.
 */
export function transitionWash(kind: TransitionKind, progress: number): number {
  const start = TRANSITION_WASH_START[kind]
  const span = 1 - start
  if (span <= 0) return 1
  const u = clampProgress((clampProgress(progress) - start) / span)
  return u * u
}

/**
 * 진행도를 0~1로 자르되 **NaN은 0으로 접는다.** `core/math.ts`의 `clamp01`은 NaN을 그대로
 * 흘리므로(그게 나머지 세 사본의 동작이다) 여기서만 한 겹 덮는다.
 *
 * 이 파일에서만 다른 이유는 결과가 화면을 덮는 먹색이기 때문이다. 먹색이 `NaN`이면 화면은
 * 100%가 아니라 "덜 검은" 상태로 서고, 교체는 100%일 때만 일어난다는 이 파일의 첫 규약이
 * 조용히 무너진다 - 깨끗한 원본과 변경본이 한 프레임 나란히 보이는 깜빡임 대조기가 된다.
 * 0으로 접으면 최소한 "아직 안 덮였다"라는 안전한 쪽으로 틀린다.
 * `transition.test.ts`가 `transitionWash('wave', NaN) === 0`으로 이 결정을 고정한다.
 */
function clampProgress(value: number): number {
  return Number.isFinite(value) ? clamp01(value) : 0
}

/* --- 타임라인 ------------------------------------------------------------------- */

/**
 * `distort` 왜곡 중(캡처 프레임만 보인다) · `blank` 먹색으로 덮는 중(건너뛰기·움직임 줄이기) ·
 * `tail` 방이 교체된 뒤 먹색이 걷히는 중 · `done` 끝.
 */
export type TransitionStage = 'distort' | 'blank' | 'tail' | 'done'

export type TransitionFrame = Readonly<{
  stage: TransitionStage
  /** 왜곡 진행 0..1. `blank` 이후로는 멈춘 값이다. */
  progress: number
  /** 먹색 blanket 0..1. 방을 교체하는 프레임에서는 언제나 1이다. */
  wash: number
  /** 쿼드 불투명도 1..0. `tail`에서만 1 미만이 된다. */
  alpha: number
}>

export type TransitionClock = Readonly<{
  /** 한 프레임 진행. **방을 교체해야 하면 `true`** (`tail` 진입 프레임에서 정확히 한 번). */
  advance: (dtMs: number) => boolean
  frame: () => TransitionFrame
  /** 지금 상태에서 가장 짧은 먹색 구간을 거쳐 끝낸다. 탭 건너뛰기. */
  skip: () => void
  isDone: () => boolean
}>

export type TransitionClockOptions = Readonly<{
  /** 왜곡을 건너뛰고 짧은 먹색 크로스페이드만 돈다(`prefers-reduced-motion`). */
  reducedMotion?: boolean
  /** 왜곡 길이(ms). 데모의 길이 슬라이더가 쓴다. */
  distortMs?: number
}>

/**
 * 전환의 시간축. **WebGL을 하나도 모른다** - 그래야 테스트가 GPU 없이 불변식을 검사할 수 있다.
 * 여기서 지키는 불변식은 하나다: `advance`가 `true`를 돌려주는 프레임의 `wash`는 언제나 1이다.
 */
export function createTransitionClock(
  kind: TransitionKind,
  options: TransitionClockOptions = {},
): TransitionClock {
  const reduced = options.reducedMotion === true
  const distortMs = Math.max(0, options.distortMs ?? DISTORT_MS)
  const tailMs = reduced ? REDUCED_TAIL_MS : TAIL_MS

  let stage: TransitionStage = reduced || distortMs <= 0 ? 'blank' : 'distort'
  let stageMs = stage === 'distort' ? distortMs : REDUCED_BLANK_MS
  let elapsedMs = 0
  /** `blank`가 시작될 때 이미 차 있던 먹색. 여기서부터 1까지만 마저 채운다. */
  let washFloor = 0
  /** `blank`/`tail`에서 얼어붙는 왜곡 진행도. */
  let frozenProgress = 0

  function stageRatio(): number {
    return stageMs <= 0 ? 1 : clampProgress(elapsedMs / stageMs)
  }

  function frame(): TransitionFrame {
    switch (stage) {
      case 'distort': {
        const progress = stageRatio()
        return { stage, progress, wash: transitionWash(kind, progress), alpha: 1 }
      }
      case 'blank': {
        const t = stageRatio()
        return {
          stage,
          progress: frozenProgress,
          wash: washFloor + (1 - washFloor) * t,
          alpha: 1,
        }
      }
      case 'tail':
        return { stage, progress: frozenProgress, wash: 1, alpha: 1 - stageRatio() }
      case 'done':
        return { stage, progress: frozenProgress, wash: 1, alpha: 0 }
    }
  }

  function enterTail(): void {
    frozenProgress = stage === 'distort' ? 1 : frozenProgress
    stage = 'tail'
    stageMs = tailMs
    elapsedMs = 0
  }

  return {
    advance(dtMs: number): boolean {
      if (stage === 'done') return false

      elapsedMs += Math.max(0, dtMs)
      if (stageRatio() < 1) return false

      if (stage === 'tail') {
        stage = 'done'
        elapsedMs = 0
        return false
      }

      // `distort`·`blank` 둘 다 여기로 온다. **방 교체는 오직 이 지점에서만 일어나고,
      // 이 순간의 wash는 정의상 1이다**(파일 머리말의 두 번째 규칙).
      enterTail()
      return true
    },

    frame,

    skip(): void {
      if (stage !== 'distort') return
      const current = frame()
      frozenProgress = current.progress
      washFloor = current.wash
      stage = 'blank'
      // 남은 왜곡 시간을 통째로 버리고 최소 먹색 구간으로 갈아 끼운다.
      stageMs = SKIP_BLANK_MS
      elapsedMs = 0
    },

    isDone: () => stage === 'done',
  }
}

/* --- 셰이더 --------------------------------------------------------------------- */

/**
 * 정점 셰이더. 행렬을 **일부러** 쓰지 않는다 - `PlaneGeometry(2,2)`의 정점이 곧 NDC라
 * 카메라 절두체·줌·회전이 어떻든 화면을 정확히 덮는다. 쿼드를 카메라에 맞춰 배치하는
 * 매 프레임 산수가 통째로 사라지고, 그 산수가 틀려 화면 귀퉁이가 새는 실패도 같이 사라진다.
 */
export const TRANSITION_VERTEX_SHADER = `
varying vec2 vUv;

void main() {
  vUv = uv;
  gl_Position = vec4(position.xy, 0.0, 1.0);
}
`

/**
 * 네 시안이 공유하는 앞부분. **`public/transition-demo.html`과 글자 단위로 같아야 한다.**
 *
 * `WR_ENCODE_SRGB`가 정의를 갈라 놓는 유일한 지점이다. 앱에서 캡처한 렌더타깃은 선형 색공간이라
 * (three는 렌더타깃에 언제나 작업 색공간으로 쓴다) 표시 공간으로 되돌려야 하고,
 * 데모의 대용 이미지는 이미 표시 공간이라 그대로 쓴다. 그 한 줄 말고는 완전히 같은 코드다.
 */
export const TRANSITION_SHADER_PRELUDE = `
precision highp float;

varying vec2 vUv;

uniform sampler2D uTex;
uniform float uProgress;
uniform float uWash;
uniform float uAlpha;
uniform float uAspect;
uniform float uSeed;
uniform vec3 uInk;
uniform vec3 uAccent;

const float WR_TAU = 6.2831853;

// sin 기반 해시를 쓰지 않는다. 큰 각을 sin에 넣는 그 방식은 폰 GPU가 프래그먼트 highp를
// mediump로 낮추는 순간 통째로 무너져 타일이 줄무늬로 뭉친다. 주 타깃이 폰이라 여기서 진다.
float wrHash(float n) {
  float x = fract((n + uSeed) * 0.1031);
  x *= x + 33.33;
  x *= x + x;
  return fract(x);
}

float wrHash2(vec2 p) {
  vec3 v = fract(vec3(p.xyx) * 0.1031 + uSeed);
  v += dot(v, v.yzx + 33.33);
  return fract((v.x + v.y) * v.z);
}

vec3 wrToDisplay(vec3 c) {
#ifdef WR_ENCODE_SRGB
  vec3 lo = c * 12.92;
  vec3 hi = 1.055 * pow(max(c, vec3(0.0)), vec3(0.4166667)) - 0.055;
  return mix(lo, hi, step(vec3(0.0031308), c));
#else
  return c;
#endif
}

vec3 wrSample(vec2 uv) {
  return wrToDisplay(texture2D(uTex, clamp(uv, vec2(0.0), vec2(1.0))).rgb);
}

vec3 wrSampleWrap(vec2 uv) {
  return wrToDisplay(texture2D(uTex, fract(uv)).rgb);
}
`

/**
 * 시안별 본문. 각 `main`은 같은 두 줄로 시작하고 끝난다.
 *
 *  - 첫 줄의 조기 반환: 꼬리 구간(`alpha < 1`)에서는 캡처를 아예 읽지 않는다.
 *    새 방 위에 겹치는 것은 순수 먹색뿐이라 원본↔변경본 크로스디졸브가 물리적으로 불가능하다.
 *  - 마지막 줄의 `mix(rgb, uInk, uWash)`: 교체 직전 화면을 100% 먹색으로 만드는 지점.
 */
export const TRANSITION_SHADER_BODY: Readonly<Record<TransitionKind, string>> = {
  /*
   * 스캔. 수평 띠가 제각각 어긋나고, 노랑 한 줄이 아래에서 위로 훑는다.
   * 어긋남은 sin 곡선이라 시작과 끝에서 0이고 한가운데서 가장 세다.
   */
  scan: `
void main() {
  if (uAlpha < 1.0) { gl_FragColor = vec4(uInk, uAlpha); return; }

  float p = uProgress;
  float tear = sin(p * 3.1415926);
  vec2 uv = vUv;

  // 띠를 진행에 따라 잘게 쪼갠다. 굵은 덩어리에서 잔줄로 가야 "신호가 잡히는" 순서로 읽힌다.
  float bands = mix(9.0, 26.0, p);
  float row = floor(uv.y * bands);
  float jitter = wrHash(row + floor(p * 14.0) * 31.0) * 2.0 - 1.0;
  uv.x += jitter * tear * 0.22;

  // 큰 덩어리 하나가 통째로 밀린다. 잔줄만 있으면 노이즈지 화면 어긋남이 아니다.
  float slab = step(0.55, wrHash(floor(vUv.y * 3.0) + floor(p * 7.0) * 13.0));
  uv.x += slab * tear * 0.06;

  // RGB 분리는 어긋난 만큼만 - 멈춘 화면에서는 0이라 상시 색번짐이 남지 않는다.
  float split = tear * 0.011;
  vec3 rgb = vec3(
    wrSampleWrap(uv + vec2(split, 0.0)).r,
    wrSampleWrap(uv).g,
    wrSampleWrap(uv - vec2(split, 0.0)).b
  );

  // 주사선. 진폭에 묶어 두었으므로 왜곡이 없는 구간에는 존재하지 않는다.
  rgb *= 1.0 - 0.09 * tear * (0.5 + 0.5 * sin(vUv.y * 620.0));

  // 노랑 한 줄이 위로 훑는다. 화면 전체에 같은 세기로 지나가므로 특정 자리를 가리키지 않는다.
  float sweep = smoothstep(0.05, 0.0, abs(vUv.y - (1.0 - p)));
  rgb = mix(rgb, uAccent, sweep * 0.5 * tear);

  gl_FragColor = vec4(mix(rgb, uInk, uWash), uAlpha);
}
`,

  /*
   * 물결. 중앙에서 링이 퍼지고 화면 전체가 느리게 출렁인다. "슈유육"에 가장 가까운 성격.
   */
  wave: `
void main() {
  if (uAlpha < 1.0) { gl_FragColor = vec4(uInk, uAlpha); return; }

  float p = uProgress;
  float amp = sin(p * 3.1415926);

  // 화면 비율을 곱해 중심 거리를 실제 원으로 만든다. 안 하면 링이 타원이 된다.
  vec2 d = vec2((vUv.x - 0.5) * uAspect, vUv.y - 0.5);
  float r = length(d);
  vec2 dir = d / max(r, 0.0001);

  // 링의 반지름이 곧 진행도다. 감쇠를 걸어 화면 가장자리에서는 잔물결로 남는다.
  float ring = sin(r * 26.0 - p * 16.0);
  vec2 uv = vUv + dir * ring * amp * exp(-r * 1.6) * 0.055;

  // 링만 있으면 파문 한 줄이지 물결이 아니다. 저주파 출렁임을 겹친다.
  uv.x += sin(vUv.y * 9.0 + p * 11.0) * amp * 0.018;
  uv.y += sin(vUv.x * 7.0 - p * 9.0) * amp * 0.014;

  float split = amp * 0.005;
  vec3 rgb = vec3(
    wrSample(uv + dir * split).r,
    wrSample(uv).g,
    wrSample(uv - dir * split).b
  );

  // 링의 마루만 살짝 밝힌다. 파도의 흰 거품 자리다.
  // 중심에는 걸지 않는다 - 화면 한가운데 고정된 밝은 점은 연출이 아니라 얼룩으로 읽힌다.
  float crest = max(ring, 0.0) * smoothstep(0.0, 0.22, r) * exp(-r * 2.0);
  rgb += uAccent * crest * amp * 0.07;

  gl_FragColor = vec4(mix(rgb, uInk, uWash), uAlpha);
}
`,

  /*
   * 셔플. 화면을 정사각 타일로 쪼개 제각각 밀어냈다가 제자리로 모은다.
   * 모이는 구간은 `TRANSITION_WASH_START.shuffle`이 이미 덮고 있다(파일 머리말의 두 번째 규칙).
   */
  shuffle: `
void main() {
  if (uAlpha < 1.0) { gl_FragColor = vec4(uInk, uAlpha); return; }

  float p = uProgress;

  // 가로 타일 수를 화면 비율로 정해 타일을 정사각에 가깝게 유지한다.
  vec2 grid = vec2(max(floor(6.0 * uAspect + 0.5), 3.0), 6.0);
  vec2 cell = floor(vUv * grid);
  float dirHash = wrHash2(cell);
  float lagHash = wrHash2(cell + 17.0);

  // 타일마다 시작이 조금씩 어긋난다. 동시에 움직이면 화면 전체가 한 덩어리로 밀린다.
  float tp = clamp(p * (1.0 + lagHash * 0.5) - lagHash * 0.18, 0.0, 1.0);
  float scatter = smoothstep(0.0, 0.40, tp) * (1.0 - smoothstep(0.58, 1.0, tp));

  float ang = dirHash * WR_TAU;
  vec2 offset = vec2(cos(ang) / uAspect, sin(ang)) * scatter * 0.30;
  vec3 rgb = wrSampleWrap(vUv + offset);

  // 타일 경계선. 이게 없으면 '조각난 화면'이 아니라 그냥 '흔들리는 화면'이다.
  vec2 g = fract(vUv * grid);
  float inner = min(min(g.x, 1.0 - g.x), min(g.y, 1.0 - g.y));
  float seam = 1.0 - smoothstep(0.0, 0.02, inner);
  rgb = mix(rgb, mix(uInk, uAccent, 0.25), seam * scatter * 0.7);

  gl_FragColor = vec4(mix(rgb, uInk, uWash), uAlpha);
}
`,

  /*
   * 줌 펀치. 앞머리에서 짧고 세게 파고들었다가 천천히 풀린다. 넷 중 가장 짧게 읽힌다.
   */
  punch: `
void main() {
  if (uAlpha < 1.0) { gl_FragColor = vec4(uInk, uAlpha); return; }

  float p = uProgress;
  float kick = smoothstep(0.0, 0.22, p) * (1.0 - smoothstep(0.30, 1.0, p));

  vec2 d = vUv - 0.5;
  vec2 uv = 0.5 + d * (1.0 - kick * 0.16);

  // 방사 블러 6탭. 탭 수가 곧 비용이라 여기서 멈춘다.
  vec3 rgb = vec3(0.0);
  float weight = 0.0;
  for (int i = 0; i < 6; i++) {
    float t = float(i) / 5.0;
    float w = 1.0 - t * 0.6;
    rgb += wrSample(uv - d * t * kick * 0.13) * w;
    weight += w;
  }
  rgb /= weight;

  // 가장자리 감쇠. 화면 전체에 같은 규칙으로 걸리므로 특정 자리를 가리키지 않는다.
  rgb *= clamp(1.0 - length(d) * kick * 1.1, 0.0, 1.0);

  gl_FragColor = vec4(mix(rgb, uInk, uWash), uAlpha);
}
`,
}

export function transitionFragmentShader(kind: TransitionKind): string {
  return TRANSITION_SHADER_PRELUDE + TRANSITION_SHADER_BODY[kind]
}

/* --- 팔레트 --------------------------------------------------------------------- */

/*
 * `gameHud.css`의 `--gh-ink` · `--gh-accent`와 같은 값이다. **`THREE.Color`를 쓰지 않는다** -
 * three의 ColorManagement가 생성자에서 선형 공간으로 바꿔 버리는데, 이 셰이더는 표시 공간에서
 * 섞으므로 그 변환이 곧 다른 색이 된다. 화면에 나갈 값을 그대로 실어 보낸다.
 * 먹색은 `scene.ts`의 `INK_HEX`가 원본이다.
 */
const INK_RGB = new THREE.Vector3(
  ((INK_HEX >> 16) & 0xff) / 0xff,
  ((INK_HEX >> 8) & 0xff) / 0xff,
  (INK_HEX & 0xff) / 0xff,
)
const ACCENT_RGB = new THREE.Vector3(0xff / 0xff, 0xd2 / 0xff, 0x3f / 0xff)

/* --- 컨트롤러 ------------------------------------------------------------------- */

/**
 * 쿼드가 그려지는 순서. 씬에서 가장 큰 값(`revealHighlight`의 900)보다 넉넉히 위에 둔다.
 * `depthTest:false`와 함께라야 HUD 아래·씬 위라는 자리가 확정된다.
 */
const QUAD_RENDER_ORDER = 10000

/**
 * 캡처 렌더타깃의 긴 변 상한(px).
 *
 * "해상도를 깎는 후처리 금지"(`scene.ts`)와 부딪히지 않는다. 저 규칙은 **술래가 눈으로 훑는
 * 화면**에 대한 것인데, 이 텍스처는 첫 프레임부터 뒤틀려 있고 마지막에는 100% 먹색으로 덮인다.
 * 아무도 이 위에서 물건을 찾지 않는다. 4K 화면에서 60MB짜리 버퍼를 매 판 잡는 쪽이 훨씬 나쁘다.
 */
const MAX_CAPTURE_LONG_SIDE = 2048

/**
 * 재생 횟수 → 연출 시드 간격. 황금비 소수부라 몇 판을 이어 해도 무늬가 반복되지 않는다.
 * **게임 상태와 무관한 값이다** - 여기서 `Math.random()`을 쓰지 않는 이유는 결정론이 아니라
 * 재현성이다(같은 순서로 플레이하면 스크린샷이 같아야 버그 신고가 성립한다).
 */
const SEED_STEP = 0.6180339887

export type RoomTransitionOptions = Readonly<{
  /** 쓸 시안. 생략하면 `DEFAULT_TRANSITION_KIND`. */
  kind?: TransitionKind
  /** 왜곡 길이(ms). 생략하면 `DISTORT_MS`. */
  distortMs?: number
  /**
   * 움직임 줄이기를 강제한다. 생략하면 매 재생마다 `prefers-reduced-motion`을 다시 묻는다
   * (캐시하면 OS 설정을 켜도 새로고침 전까지 안 먹는다).
   */
  reducedMotion?: boolean
  /** 연출 난수 시드. 게임 상태와 무관하며 생략하면 재생 횟수를 쓴다(결정론 유지). */
  seed?: number
}>

export type RoomTransition = Readonly<{
  /**
   * 전환을 시작한다. **지금 화면에 있는 프레임을 그 자리에서 캡처한다** -
   * 부르기 전에 씬은 아직 원본 방이어야 한다.
   *
   * @param onSwap 왜곡이 끝나는 순간 정확히 한 번 불린다. 여기서 방을 갈아 끼워라.
   * @returns 이펙트가 실제로 섰는가. **`false`면 방 교체를 대신 해 주지 않는다** -
   *          호출부가 원래 쓰던 교체 경로를 그대로 태워야 한다.
   */
  play: (onSwap: () => void) => boolean
  /** 한 프레임 진행(초). 세션의 프레임 루프가 부른다. */
  update: (dtSeconds: number) => void
  isPlaying: () => boolean
  /** 짧은 먹색 구간을 거쳐 접는다. 탭 건너뛰기와 같은 경로. */
  skip: () => void
  /** 지금 당장 끝낸다. 대기 중인 방 교체를 **반드시** 실행한다(라운드 재시작·모드 전환용). */
  finish: () => void
  dispose: () => void
}>

/**
 * 외우기 → 수색 전환 이펙트를 만든다. 판마다 하나씩 만들고 `dispose()`로 접는다.
 *
 * 만드는 시점에는 GPU 자원을 잡지 않는다 - 첫 `play()`에서 잡고 끝나면 놓는다.
 * 수색 5분 내내 풀스크린 렌더타깃을 쥐고 있을 이유가 없다.
 */
export function createRoomTransition(
  ctx: SceneCtx,
  options: RoomTransitionOptions = {},
): RoomTransition {
  const kind = options.kind ?? DEFAULT_TRANSITION_KIND
  const canvas = ctx.renderer.domElement

  /**
   * 유니폼을 이름으로 꺼내 쓰지 않고 이 레코드를 통째로 들고 다닌다.
   * `ShaderMaterial.uniforms`는 색인 시그니처라 `noUncheckedIndexedAccess` 아래에서
   * 모든 접근이 `undefined` 가능이 되고, 그때마다 붙는 `!`가 곧 오타를 통과시키는 구멍이다.
   */
  type Uniforms = {
    uTex: THREE.IUniform<THREE.Texture | null>
    uProgress: THREE.IUniform<number>
    uWash: THREE.IUniform<number>
    uAlpha: THREE.IUniform<number>
    uAspect: THREE.IUniform<number>
    uSeed: THREE.IUniform<number>
    uInk: THREE.IUniform<THREE.Vector3>
    uAccent: THREE.IUniform<THREE.Vector3>
  }

  let target: THREE.WebGLRenderTarget | null = null
  let quad: THREE.Mesh | null = null
  let material: THREE.ShaderMaterial | null = null
  let geometry: THREE.PlaneGeometry | null = null
  let uniforms: Uniforms | null = null

  let clock: TransitionClock | null = null
  let pendingSwap: (() => void) | null = null
  let playCount = 0

  /** 쿼드는 한 번만 만들어 판이 끝날 때까지 쓴다. 매 재생마다 다시 잡는 것은 렌더타깃뿐이다. */
  function ensureQuad(): Uniforms {
    if (uniforms !== null) return uniforms

    const created: Uniforms = {
      uTex: { value: null },
      uProgress: { value: 0 },
      uWash: { value: 0 },
      uAlpha: { value: 1 },
      uAspect: { value: 1 },
      uSeed: { value: 0 },
      uInk: { value: INK_RGB },
      uAccent: { value: ACCENT_RGB },
    }

    geometry = new THREE.PlaneGeometry(2, 2)
    material = new THREE.ShaderMaterial({
      vertexShader: TRANSITION_VERTEX_SHADER,
      fragmentShader: transitionFragmentShader(kind),
      // 렌더타깃은 선형 색공간이다(three는 렌더타깃에 작업 색공간으로 쓴다). 셰이더가 되돌린다.
      defines: { WR_ENCODE_SRGB: '' },
      uniforms: created,
      // 꼬리 구간에서만 섞기가 필요하다. 왜곡 구간은 불투명이라 씬을 완전히 가린다.
      transparent: true,
      depthTest: false,
      depthWrite: false,
    })
    uniforms = created

    const mesh = new THREE.Mesh(geometry, material)
    mesh.name = 'room-transition-quad'
    mesh.renderOrder = QUAD_RENDER_ORDER
    // 정점 셰이더가 행렬을 무시하므로 절두체 판정에 걸릴 이유도, 걸려서도 안 된다.
    mesh.frustumCulled = false
    mesh.castShadow = false
    mesh.receiveShadow = false
    mesh.visible = false
    ctx.scene.add(mesh)
    quad = mesh

    return created
  }

  /** 캔버스 비율을 유지한 채 상한 안으로 줄인 캡처 크기. 비율이 어긋나면 화면이 늘어난다. */
  function captureSize(): { width: number; height: number } {
    const buffer = ctx.renderer.getDrawingBufferSize(new THREE.Vector2())
    const width = Math.max(1, Math.floor(buffer.x))
    const height = Math.max(1, Math.floor(buffer.y))
    const scale = Math.min(1, MAX_CAPTURE_LONG_SIDE / Math.max(width, height))
    return {
      width: Math.max(1, Math.round(width * scale)),
      height: Math.max(1, Math.round(height * scale)),
    }
  }

  /**
   * 지금 씬을 렌더타깃에 굳힌다. **이 한 번의 추가 렌더가 이 기능의 유일한 프레임 비용이다** -
   * 페이즈 경계에서 한 번뿐이라 감수한다. 쿼드는 스스로를 찍지 않도록 잠깐 숨긴다.
   */
  function capture(): THREE.WebGLRenderTarget {
    // 이전 판의 버퍼가 남아 있으면 여기서 놓는다. 크기가 바뀌었을 수도 있다(회전·주소창 접힘).
    releaseTarget()

    const size = captureSize()
    const created = new THREE.WebGLRenderTarget(size.width, size.height, {
      depthBuffer: true,
      stencilBuffer: false,
    })
    created.texture.minFilter = THREE.LinearFilter
    created.texture.magFilter = THREE.LinearFilter
    created.texture.generateMipmaps = false
    target = created

    const wasVisible = quad?.visible ?? false
    if (quad !== null) quad.visible = false

    const previous = ctx.renderer.getRenderTarget()
    ctx.renderer.setRenderTarget(created)
    ctx.renderer.render(ctx.scene, ctx.camera)
    ctx.renderer.setRenderTarget(previous)

    if (quad !== null) quad.visible = wasVisible

    return created
  }

  function releaseTarget(): void {
    target?.dispose()
    target = null
    if (uniforms !== null) uniforms.uTex.value = null
  }

  function applyFrame(frame: TransitionFrame): void {
    if (uniforms === null) return
    uniforms.uProgress.value = frame.progress
    uniforms.uWash.value = frame.wash
    uniforms.uAlpha.value = frame.alpha
  }

  /** 대기 중인 방 교체를 정확히 한 번 실행한다. 두 번 부를 수 있는 경로가 여럿이라 잠금이 필요하다. */
  function commitSwap(): void {
    const swap = pendingSwap
    pendingSwap = null
    swap?.()
  }

  function teardown(): void {
    clock = null
    if (quad !== null) quad.visible = false
    detachSkipListener()
    // 풀스크린 버퍼를 판 내내 쥐고 있지 않는다. 다음 재생에서 다시 잡는다.
    releaseTarget()
  }

  function finish(): void {
    if (clock === null) return
    commitSwap()
    teardown()
  }

  /* --- 탭 건너뛰기 ---------------------------------------------------------- */

  /*
   * `window`의 캡처 단계에 건다. 전파 경로의 맨 앞이라 캔버스·문서에 걸린 어떤 핸들러보다 먼저 돈다.
   * **캔버스를 찍은 탭만 삼킨다**(`stopImmediatePropagation`) - 안 그러면 건너뛰려던 탭이
   * 그대로 지목이 되어 탄환 한 발이 아무 데나 날아간다. HUD 버튼을 찍은 탭은 그대로 흘려보내
   * 전환 중에도 설정·재장전이 죽지 않게 한다.
   */
  function onSkipPointer(event: Event): void {
    if (clock === null) return
    if (event.target === canvas) {
      event.stopImmediatePropagation()
      event.preventDefault()
    }
    skip()
  }

  function attachSkipListener(): void {
    window.addEventListener('pointerdown', onSkipPointer, { capture: true })
  }

  function detachSkipListener(): void {
    window.removeEventListener('pointerdown', onSkipPointer, { capture: true })
  }

  /** 컨텍스트가 날아가면 이펙트는 끝난 것이다. 그래도 **방 교체는 반드시 일어나야 한다.** */
  function onContextLost(): void {
    finish()
  }

  canvas.addEventListener('webglcontextlost', onContextLost)

  function skip(): void {
    if (clock === null) return
    clock.skip()
    applyFrame(clock.frame())
  }

  function play(onSwap: () => void): boolean {
    // 겹쳐 부르는 경로(빠른 재시작)에서 앞의 전환이 방 교체를 잃지 않게 먼저 마무리한다.
    finish()

    try {
      if (ctx.renderer.getContext().isContextLost()) return false

      const slots = ensureQuad()
      const captured = capture()

      playCount += 1
      slots.uTex.value = captured.texture
      slots.uAspect.value = captured.width / Math.max(1, captured.height)
      // 연출용 난수다. 재생 횟수에서 뽑으므로 같은 순서로 플레이하면 같은 무늬가 나온다.
      slots.uSeed.value = options.seed ?? playCount * SEED_STEP

      clock = createTransitionClock(kind, {
        reducedMotion: options.reducedMotion ?? prefersReducedMotion(),
        distortMs: options.distortMs,
      })
      pendingSwap = onSwap
      applyFrame(clock.frame())
      if (quad !== null) quad.visible = true
      attachSkipListener()

      return true
    } catch (error) {
      // 저사양·컨텍스트 소진 등으로 이펙트가 서지 못했다. **판을 막지 않는다.**
      console.warn('[transition] 전환 이펙트를 세우지 못해 건너뛴다.', error)
      pendingSwap = null
      teardown()
      return false
    }
  }

  function update(dtSeconds: number): void {
    if (clock === null) return

    try {
      const swap = clock.advance(dtSeconds * MS_PER_SECOND)
      // 순서가 규약이다. 교체 프레임의 화면은 100% 먹색이고, 그 아래에서 방이 바뀐다.
      if (swap) commitSwap()

      applyFrame(clock.frame())
      if (clock.isDone()) teardown()
    } catch (error) {
      console.warn('[transition] 전환 진행 중 예외가 발생해 즉시 끝낸다.', error)
      finish()
    }
  }

  return {
    play,
    update,
    isPlaying: () => clock !== null,
    skip,
    finish,

    dispose(): void {
      // 대기 중인 교체가 남아 있으면 실행한다. 이펙트가 사라졌다고 방이 안 바뀌면 그건 진행 불능이다.
      finish()
      canvas.removeEventListener('webglcontextlost', onContextLost)
      detachSkipListener()

      if (quad !== null) ctx.scene.remove(quad)
      quad = null
      geometry?.dispose()
      geometry = null
      material?.dispose()
      material = null
      releaseTarget()
      uniforms = null
    },
  }
}
